import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { db } from "../../../../firebase";
import { set, ref } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Typography,
  Modal,
  CircularProgress,
} from "@mui/material";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { Formik } from "formik";
import {
  decodeClientToken,
  decodeTokenRefresh,
  deleteToken,
  getClientToken,
  getTokenRefresh,
  verifyExpireTokenRefresh,
} from "../../../../helpers/token/refreshToken";
import {
  addBooking,
  deleteBooking,
} from "../../../../features/booking/bookingSlice";
import { getAddressAndLatLng } from "../../../../api/expandUrl";
import SnackBar from "../../../../components/snackbar/SnackBar";

import "../booking.css";
import { circularProgressStyle } from "./BookingStyle";
import {
  dateFormat,
  formatDateTime,
  timeFormat,
} from "../../../../helpers/date/date";
import { sendICSEmailNow } from "../../../../api/resources/ics/ics";
import { getListCountryUseCase } from "./usecases/country/GetListCountryUseCase";
import {
  getListTourAll,
  getListTourByCountry,
} from "./data/repository/tourRepo";
import { GetFrequentClient } from "./usecases/GetFrecuentClient";
import { CreateBackupUseCase } from "./usecases/backup/CreateBackupUseCase";
import { GetCounterRider } from "./usecases/GetCounterRider";
import loadingStyle from "./style/loading.module.css";
// import { SendNotificationsDriver } from "./domain/SendNotificationsDriver";
import { SendNotificationsDriverV2 } from "./domain/SendNotificationsDriverV2";
import { CleaningServices } from "@mui/icons-material";
import { EstimateFareUseCase } from "./usecases/fare/EstimateFareUseCase";
import BookingDetailsFields from "./pages/components/BookingDetailsFields";
import DestinationAutocompleteField from "./pages/components/DestinationAutocompleteField";
import OffersModal from "./pages/components/OffersModal";
import OriginAutocompleteField from "./pages/components/OriginAutocompleteField";
import OriginMapModal from "./pages/components/OriginMapModal";
import OtherPersonModal from "./pages/components/OtherPersonModal";
import RadarAnimation from "./pages/components/RadarAnimation";
import SubRoutesModal from "./pages/components/SubRoutesModal";
import BookingSuccessModal from "./pages/components/BookingSuccessModal";
import { useBookingOffers } from "./pages/hooks/useBookingOffers";
import { useBookingLocations } from "./pages/hooks/useBookingLocations";
import { useBookingSnackbar } from "./pages/hooks/useBookingSnackbar";
import { useRiderSelection } from "./pages/hooks/useRiderSelection";

const getEstimateFareTotal = (response) =>
  response?.data?.total ?? response?.total ?? null;

const hasValidCoordinate = (coordinate) =>
  Number.isFinite(Number(coordinate?.lat)) &&
  Number.isFinite(Number(coordinate?.lng)) &&
  (Number(coordinate.lat) !== 0 || Number(coordinate.lng) !== 0);

const hasTwoWords = (value) => value.trim().split(/\s+/).length >= 2;

const isMacPlatform = () => {
  const platform =
    navigator.userAgentData?.platform || navigator.platform || "";
  return /mac/i.test(platform);
};

const bookingSuccessMessages = {
  2: "RESERVA CREADA CORRECTAMENTE",
  3: "RESERVA POR HORA CREADO CORRECTAMENTE",
  4: "RESERVA DE TOUR CREADO CORRECTAMENTE",
  5: "COTIZACION CREADA CORRECTAMENTE",
};

const defaultBookingFormValues = {
  persons: 1,
  vehicle: "",
  booking: "",
  note: "",
};

const FareEstimator = ({
  values,
  coordOrigin,
  coordDestination,
  routeMetrics,
  setFieldValue,
}) => {
  useEffect(() => {
    const estimateFare = async () => {
      if (!routeMetrics || !values.vehicle) return;

      try {
        const response = await EstimateFareUseCase({
          origin: {
            lat: coordOrigin.lat,
            lng: coordOrigin.lng,
          },
          destination: {
            lat: coordDestination.lat,
            lng: coordDestination.lng,
          },
          vehicleId: values.vehicle,
          date: new Date().toLocaleDateString("en-CA"),
          time: new Date().toTimeString().slice(0, 8),
          distanceKm: routeMetrics.distanceKm,
          durationMinutes: routeMetrics.durationMinutes,
          nationality: 1,
        });
        const total = getEstimateFareTotal(response);
        if (total !== null) {
          setFieldValue("total", total);
        }
      } catch (error) {
        console.log(error);
      }
    };

    estimateFare();
  }, [
    values.vehicle,
    coordOrigin.lat,
    coordOrigin.lng,
    coordDestination.lat,
    coordDestination.lng,
    routeMetrics,
    setFieldValue,
  ]);

  return null;
};

const BookingNow = ({ model }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bookingState = useSelector((state) => state.bookings);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [timeEnd, setTimeEnd] = useState(null);

  const {
    addressDestination,
    setAddressDestination,
    coordDestination,
    setCoordDestination,
    subRutes,
    openSubRutesModal,
    subRouteSearch,
    setSubRouteSearch,
    subRouteError,
    setSubRouteError,
    setSubRouteSelected,
    routeMetrics,
    addressOrigin,
    setAddressOrigin,
    coordOrigin,
    setCoordOrigin,
    destinationInputValue,
    handSelect,
    handleSelectDestination,
    handleSelectDestinationPoint,
    handleOpenDestinationRoutes,
    handleCloseSubRutesModal,
    handleSelectSubRoute,
    handleSelectSubRoutePoint,
    handleAcceptSubRoute,
    handleRemoveSubRoute,
    handleSubRutesDragOver,
    handleSubRutesDragEnd,
    resetBookingLocations,
  } = useBookingLocations({ bookingState });

  const [serviceList, setServiceList] = useState(
    bookingState?.data ? bookingState?.data.serviceList : [],
  );
  const [route1, setRoute1] = useState("");
  const [indice, setIndice] = useState(-1);
  const [fieldErrors, setFieldErrors] = useState({});

  const initialValues = bookingState?.data
    ? bookingState?.data
    : defaultBookingFormValues;

  const [isOrigin, setIsOrigin] = useState(false);

  const [openIntermediate, setOpenIntermediate] = useState(false);
  const [isIntermediate, setIsIntermediate] = useState(false);
  const [intermediateURL, setIntermediateURL] = useState("");

  const [url, setUrl] = useState("");

  const [openLocationModal, setOpenLocationModal] = useState(false);
  const [stateLocation, setStateLocation] = useState(true);
  const handleOpenLocationModal = (state) => {
    setOpenLocationModal(true);
    setStateLocation(state);
  };
  const handleCloseLocationModal = () => setOpenLocationModal(false);

  const handleOpenIntermediate = () => setOpenIntermediate(true);
  const handleCloseIntermediate = () => setOpenIntermediate(false);

  const handleLocation = async () => {
    try {
      const getAddress = await getAddressAndLatLng(url);
      if (stateLocation) {
        setAddressOrigin(getAddress?.data?.address);
        setCoordOrigin({
          lat: getAddress?.data?.lat,
          lng: getAddress?.data?.lng,
        });
      } else {
        setAddressDestination(getAddress?.data?.address);
        setCoordDestination({
          lat: getAddress?.data?.lat,
          lng: getAddress?.data?.lng,
        });
      }
      handleCloseLocationModal();
    } catch (error) {
      handleCloseLocationModal();
      console.log(error);
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingBookingRef = useRef(false);
  const [openRadarAnimation, setOpenRadarAnimation] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState({
    open: false,
    message: "",
    orderCounter: "",
  });
  const [openOriginMapModal, setOpenOriginMapModal] = useState(false);
  const [openDestinationMapModal, setOpenDestinationMapModal] = useState(false);
  const [openSubRouteMapModal, setOpenSubRouteMapModal] = useState(false);
  const [subRouteMapCoordinate, setSubRouteMapCoordinate] = useState(null);
  const {
    stateSnk,
    vertical,
    horizontal,
    severity,
    messageSnk,
    handleSnkClose,
    snackBarDialog,
  } = useBookingSnackbar();
  const {
    riderOption,
    openOtherPersonModal,
    otherPersonName,
    otherPersonPhone,
    otherPersonPreview,
    handleRiderOptionChange,
    handleCloseOtherPersonModal,
    handleOpenOtherPersonModal,
    handleOtherPersonNameChange,
    handleOtherPersonPhoneChange,
    handleAcceptOtherPerson,
    resetRiderSelection,
  } = useRiderSelection({ snackBarDialog });
  const {
    bookingOfferDetail,
    isBookingActive,
    driverSelect,
    openOffers,
    offersList,
    cancellationReasons,
    handleOpenOffer,
    handleCloseOffer,
    acceptCounterOffer,
    cancelBooking,
  } = useBookingOffers();

  const clearFieldError = (field) => {
    setFieldErrors((currentErrors) => {
      if (!currentErrors[field]) return currentErrors;
      const nextErrors = { ...currentErrors };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const getBookingDestination = () => {
    const lastSubRoute = subRutes[subRutes.length - 1];
    if (lastSubRoute) {
      return {
        name: lastSubRoute.name,
        lat: lastSubRoute.lat,
        lng: lastSubRoute.lng,
      };
    }

    return {
      name: addressDestination,
      lat: coordDestination.lat,
      lng: coordDestination.lng,
    };
  };

  const getBookingSubRoutes = () =>
    subRutes.slice(0, -1).map((subRoute, index) => ({
      ...subRoute,
      id: index + 1,
    }));

  const getReservationMissingField = (values, bookingDestination) => {
    const validations = [
      {
        field: "riderOption",
        isInvalid: riderOption === 0,
        message: "SELECCIONE PARA QUIEN ES EL SERVICIO",
      },
      {
        field: "otherPersonName",
        isInvalid: riderOption === 2 && !hasTwoWords(otherPersonPreview.name),
        message: "INGRESE NOMBRE Y APELLIDO",
        onInvalid: handleOpenOtherPersonModal,
      },
      {
        field: "otherPersonPhone",
        isInvalid: riderOption === 2 && !otherPersonPreview.phone.trim(),
        message: "INGRESE EL TELEFONO",
        onInvalid: handleOpenOtherPersonModal,
      },
      {
        field: "origin",
        isInvalid: !hasValidCoordinate(coordOrigin),
        message: "Ingrese el Origen del servicio",
      },
      {
        field: "destination",
        isInvalid: !hasValidCoordinate(bookingDestination),
        message: "Ingrese el Destino del servicio",
      },
      {
        field: "persons",
        isInvalid: !values.persons || Number(values.persons) <= 0,
        message: "INGRESE LA CANTIDAD DE PERSONAS",
      },
      {
        field: "vehicle",
        isInvalid: !values.vehicle,
        message: "SELECCIONE UN VEHICULO",
      },
      {
        field: "booking",
        isInvalid: !values.booking,
        message: "SELECCIONE UNA OPCION",
      },
      {
        field: "date",
        isInvalid: values.booking !== 1 && !date,
        message: "SELECCIONE LA FECHA",
      },
      {
        field: "time",
        isInvalid: values.booking !== 1 && !time,
        message: "SELECCIONE LA HORA",
      },
      {
        field: "dateEnd",
        isInvalid: values.booking === 3 && !dateEnd,
        message: "SELECCIONE LA FECHA FINAL",
      },
      {
        field: "timeEnd",
        isInvalid: values.booking === 3 && !timeEnd,
        message: "SELECCIONE LA HORA FINAL",
      },
      {
        field: "country",
        isInvalid: values.booking === 4 && !country,
        message: "SELECCIONE EL PAIS",
      },
      {
        field: "tour",
        isInvalid: values.booking === 4 && !tour,
        message: "SELECCIONE EL TOUR",
      },
      {
        field: "destination",
        isInvalid: !routeMetrics,
        message: "NO SE PUDO CALCULAR DISTANCIA Y DURACION",
      },
    ];

    return validations.find(({ isInvalid }) => isInvalid);
  };

  const validateReservationFields = (values, bookingDestination) => {
    const missingField = getReservationMissingField(values, bookingDestination);
    if (!missingField) {
      setFieldErrors({});
      return true;
    }

    setFieldErrors({ [missingField.field]: true });
    snackBarDialog(missingField.message, "error");
    missingField.onInvalid?.();
    return false;
  };

  const handleCloseRadarAnimation = () => setOpenRadarAnimation(false);
  const handleCloseBookingSuccess = () => {
    setBookingSuccess((currentSuccess) => ({
      ...currentSuccess,
      open: false,
    }));
  };
  const handleViewBookingHistory = () => {
    handleCloseBookingSuccess();
    navigate("/client/booking");
  };
  const resetBookingForm = (resetForm) => {
    resetForm({ values: defaultBookingFormValues });
    resetBookingLocations();
    resetRiderSelection();
    setServiceList([]);
    setDate(null);
    setTime(null);
    setDateEnd(null);
    setTimeEnd(null);
    setCountry(null);
    setTour(null);
    setTours([]);
    setFieldErrors({});
  };

  const handleOpenOriginMapModal = async () => {
    const originSearch = String(addressOrigin || "").trim();

    if (originSearch && !hasValidCoordinate(coordOrigin)) {
      try {
        await handSelect(originSearch);
      } catch (error) {
        console.log(error);
        snackBarDialog("No se pudo ubicar el origen escrito", "error");
      } finally {
        setOpenOriginMapModal(true);
      }
      return;
    }

    if (originSearch || isMacPlatform() || !navigator.geolocation) {
      setOpenOriginMapModal(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const point = {
          lat: coords.latitude,
          lng: coords.longitude,
        };
        setAddressOrigin("");
        setCoordOrigin(point);
        setOpenOriginMapModal(true);
      },
      () => {
        snackBarDialog("No se pudo obtener la ubicacion actual", "error");
        setOpenOriginMapModal(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };
  const handleCloseOriginMapModal = () => setOpenOriginMapModal(false);
  const handleOpenDestinationMapModal = () => {
    if (isMacPlatform() || !navigator.geolocation) {
      setOpenDestinationMapModal(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const point = {
          lat: coords.latitude,
          lng: coords.longitude,
        };
        setAddressDestination("");
        setCoordDestination(point);
        setOpenDestinationMapModal(true);
      },
      () => {
        snackBarDialog("No se pudo obtener la ubicacion actual", "error");
        setOpenDestinationMapModal(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };
  const handleCloseDestinationMapModal = () =>
    setOpenDestinationMapModal(false);
  const handleOpenSubRouteMapModal = () => {
    if (isMacPlatform() || !navigator.geolocation) {
      setOpenSubRouteMapModal(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const point = {
          lat: coords.latitude,
          lng: coords.longitude,
        };
        setSubRouteSearch("");
        setSubRouteSelected(null);
        setSubRouteMapCoordinate(point);
        setOpenSubRouteMapModal(true);
      },
      () => {
        snackBarDialog("No se pudo obtener la ubicacion actual", "error");
        setOpenSubRouteMapModal(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };
  const handleCloseSubRouteMapModal = () => {
    setOpenSubRouteMapModal(false);
    setSubRouteMapCoordinate(null);
  };

  const handleFormSubmit = async (values, { resetForm }) => {
    const bookingDestination = getBookingDestination();
    const bookingSubRoutes = getBookingSubRoutes();

    if (!validateReservationFields(values, bookingDestination)) {
      return;
    }

    if (hasValidCoordinate(coordOrigin)) {
      if (hasValidCoordinate(bookingDestination)) {
        const verify = verifyExpireTokenRefresh();
        if (!verify) {
          if (values.booking !== 1) {
            const today = new Date();
            const { day, month, year, hour, minute } = formatDateTime(
              date.$d,
              time.$d,
            );
            const dateOrder = new Date(year, month, day, hour, minute, 0);
            const diffInMillisec = dateOrder.getTime() - today.getTime();
            const diffInMin = diffInMillisec / (1000 * 60);
            if (values.booking != 1 && diffInMin <= 30) {
              snackBarDialog(
                "TODA RESERVA DEBE SER MAYOR A 30 MINUTOS",
                "error",
              );
              setFieldErrors({ date: true, time: true });
              return;
            }
          }

          if (isSubmittingBookingRef.current) {
            return;
          }

          isSubmittingBookingRef.current = true;
          setIsLoading(true);
          setOpenRadarAnimation(true);
          try {
            const uuid = uuidv4();
            const nDate = new Date(Date.now());

            const data = createDataMySql(
              values,
              uuid,
              nDate,
              bookingDestination,
              bookingSubRoutes,
            );
            
            const backup = await CreateBackupUseCase(data);
     
            if (backup.message === "success") {
              const orderNumber = backup.insertId;
              const fData = createDataFirebase(
                values,
                uuid,
                orderNumber,
                nDate,
              );

              const bookingCreated = await createBookingFirebase(fData);
              if (bookingCreated) {
                resetBookingForm(resetForm);
              }

              if (bookingCreated && values.booking == 1) {
                // await SendNotificationsDriver();
                // Version 2 de prueba. Produccion sigue usando SendNotificationsDriver().
                await SendNotificationsDriverV2({
                  notifyTo:"DRIVER",
                  orderNumber,
                  latitude: coordOrigin.lat,
                  longitude: coordOrigin.lng,
                });
              }

            } else {
              snackBarDialog("ERROR: No se creo el Pedido", "error");
            }
          } catch (error) {
            console.log(error);
            snackBarDialog("ERROR: No se creo el Pedido", "error");
          } finally {
            isSubmittingBookingRef.current = false;
            setIsLoading(false);
            setOpenRadarAnimation(false);
          }
        } else {
          deleteToken("ctknre");
          deleteToken("cdttkn");
          const origin = {
            name: addressOrigin,
            lat: coordOrigin.lat,
            lng: coordOrigin.lng,
          };
          const destination = {
            name: bookingDestination.name,
            lat: bookingDestination.lat,
            lng: bookingDestination.lng,
          };
          dispatch(
            addBooking({
              ...values,
              id: uuidv4(),
              origin,
              destination,
              serviceList,
            }),
          );
          navigate("/login");
        }
      } else {
        setFieldErrors({ destination: true });
        snackBarDialog("Ingrese el Destino del servicio", "error");
      }
    } else {
      setFieldErrors({ origin: true });
      snackBarDialog("Ingrese el Origen del servicio", "error");
    }
  };

  const [frequent, setFrequent] = useState(false);
  useEffect(() => {
    const getFrequentClient = async () => {
      try {
        const token = getTokenRefresh();
        const uid = decodeTokenRefresh(token);
        const response = await GetFrequentClient(uid);
        const counterResp = await GetCounterRider(uid);

        if (response.message == "success") {
          setFrequent(response.frequent);
        } else {
          console.log("error");
        }
      } catch (error) {
        console.log(error);
      }
    };
    getFrequentClient();
  }, []);

  const createDataFirebase = (
    values,
    uuid,
    counter,
    nDate,
    bookingDestination = getBookingDestination(),
    bookingSubRoutes = getBookingSubRoutes(),
  ) => {
    const token = getTokenRefresh();
    const uid = decodeTokenRefresh(token);
    const clientToken = getClientToken();
    const { name, lastname, phone, email } = decodeClientToken(clientToken);

    return {
      uuid,
      client: uid,
      clientName: `${name} ${lastname}`,
      clientEmail: email,
      clientPhone: phone,
      origin: {
        name: addressOrigin,
        lat: coordOrigin.lat,
        lng: coordOrigin.lng,
      },
      destination: {
        name: bookingDestination.name,
        lat: bookingDestination.lat,
        lng: bookingDestination.lng,
      },
      subRoutes: bookingSubRoutes,
      otherPersonName: riderOption === 2 ? otherPersonName.trim() : "",
      otherPersonPhone: riderOption === 2 ? otherPersonPhone.trim() : "",
      intermediate: serviceList,
      persons: values.persons,
      vehicle: values.vehicle,
      date: values.booking === 1 ? dateFormat(nDate) : dateFormat(date.$d),
      time: values.booking === 1 ? timeFormat(nDate) : timeFormat(time.$d),
      dateEnd: values.booking === 3 ? dateFormat(dateEnd.$d) : "",
      timeEnd: values.booking === 3 ? timeFormat(timeEnd.$d) : "",
      tour_id: values.booking === 4 ? tour.tour_id : "",
      tour_description: values.booking === 4 ? tour.description : "",
      note: values.note ? values.note : "",
      noteAdmin: "",
      currency: 1,
      payment_type: 1,
      payment_voucher: 1,
      tax: values.tax ? values.tax : 0,
      toll: 0,
      price: values.price ? values.price : 0,
      total: values.total ? values.total : 0,
      discount: 0,
      advance: 0,
      statusService: "PENDIENTE", //estado asignacion de conductor
      statusPayment: "PENDIENTE", //estado de pago del servicio servicio
      statusBooking: "PENDIENTE", //estado de pago para el conductor
      createBooking: "BOOKING",
      booking_type: values.booking === 5 ? 2 : values.booking,
      counterBooking: counter,
      created: Date.now(),
      isCreated: false,
      cotizado: frequent && values.booking != 1 ? true : false,
      phoneActive: true,
    };
  };

  const createDataMySql = (
    values,
    uuid,
    nDate,
    bookingDestination = getBookingDestination(),
    bookingSubRoutes = getBookingSubRoutes(),
  ) => {
    const token = getTokenRefresh();
    const uid = decodeTokenRefresh(token);
    return {
      uuid,
      userType: 1,
      originName: addressOrigin,
      originLat: coordOrigin.lat,
      originLng: coordOrigin.lng,
      originUrl: "",
      destinationName: bookingDestination.name,
      destinationLat: bookingDestination.lat,
      destinationLng: bookingDestination.lng,
      destinationUrl: "",
      subRoutes: bookingSubRoutes,
      otherPersonName: riderOption === 2 ? otherPersonName.trim() : "",
      otherPersonPhone: riderOption === 2 ? otherPersonPhone.trim() : "",
      date: values.booking === 1 ? dateFormat(nDate) : dateFormat(date.$d),
      time: values.booking === 1 ? timeFormat(nDate) : timeFormat(time.$d),
      dateEnd: values.booking === 3 ? dateFormat(dateEnd.$d) : "",
      timeEnd: values.booking === 3 ? timeFormat(timeEnd.$d) : "",
      persons: values.persons,
      vehicleModel: values.vehicle,
      notes: values.note ? values.note.toUpperCase() : "",
      notesAdmin: values.booking === 4 ? `${tour.code} ${tour.title}` : "",
      userId: uid,
      driverId: 0,
      vehicleId: 0,
      paymentId: 0,
      bookingType: values.booking === 5 ? 2 : values.booking,
      tourId: values.booking === 4 ? tour.id : 0,
      invoiceId: 0,
      createdBy: "BOOKING",
      status: 1,
      statePayment: "PENDIENTE",
      assigned: false,
      invoice: false,
      invoiceStatus: false,
      travelStatus: 1,
      currency: 1,
      paymentType: 1,
      paymentVoucher: 1,
      tax: values.tax ? values.tax : 0,
      toll: 0,
      price: values.price ? values.price : 0,
      total: values.total ? values.total : 0,
      discount: 0,
      advance: 0,
      quote: frequent && values.booking != 1 ? true : false,
    };
  };

  const createBookingFirebase = async (data) => {
    const dbRef = `bookingNow/${data.uuid}`;
    return set(ref(db, dbRef), data)
      .then(async () => {
        if (data.booking_type === 1) {
          handleOpenOffer();
          const dataEmail = {
            name: data.clientName,
            pedido: data.counterBooking,
            date: data.date,
            time: data.time,
            origen: data.origin.name,
            destino: data.destination.name,
            email: data.clientEmail,
            telefono: data.clientPhone,
            persons: data.persons,
            vehicle: data.vehicle,
            notas: data.note,
            web: "Booking",
          };
          // await sendICSEmailNow("token", dataEmail);
        }

        if (bookingState?.data) {
          dispatch(deleteBooking(bookingState?.data));
        }
        const successMessage = bookingSuccessMessages[data.booking_type];
        if (successMessage) {
          setBookingSuccess({
            open: true,
            message: successMessage,
            orderCounter: data.counterBooking,
          });
        } else {
          snackBarDialog("PEDIDO CREADO, ver Historial");
        }
        return true;
      })
      .catch((error) => {
        console.log(error);
        snackBarDialog("ERROR: No se creo el Pedido", "error");
        return false;
      });
  };

  const handleMouseDownPassword = (event) => event.preventDefault();

  // TOUR
  const [country, setCountry] = useState(null);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const getCountrys = async () => {
      try {
        const response = await getListCountryUseCase();
        setCountries([]);
        setCountries(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    getCountrys();
  }, []);

  //SELECCIONA UN PAIS TRAER LISTA DE TOUR
  const [tour, setTour] = useState(null);
  const [tours, setTours] = useState([]);

  useEffect(() => {
    if (country) {
      const getListTour = async () => {
        try {
          console.log(country.id);
          const response = await getListTourAll();
          setTours([]);
          setTour(null);
          setTours(response.data.tour);
        } catch (error) {
          console.log(error);
        }
      };
      getListTour();
    }
  }, [country]);

  return (
    <>
      <Formik onSubmit={handleFormSubmit} initialValues={initialValues}>
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <FareEstimator
              values={values}
              coordOrigin={coordOrigin}
              coordDestination={coordDestination}
              routeMetrics={routeMetrics}
              setFieldValue={setFieldValue}
            />
            <Box
              onSubmit={handleSubmit}
              sx={{ mt: 1, margin: "0px 0px 3px 0px" }}
            >
              <Grid container spacing={1} sx={{ alignItems: "center" }}>
                <Grid item xs={12} sm={riderOption == 2 ? 6 : 12}>
                  <FormControl fullWidth error={!!fieldErrors.riderOption}>
                    <InputLabel>Para quien es el Servicio</InputLabel>
                    <Select
                      value={riderOption}
                      id="riderOption"
                      name="riderOption"
                      label="Para quien es el Servicio"
                      onChange={(event) => {
                        clearFieldError("riderOption");
                        handleRiderOptionChange(event);
                      }}
                    >
                      <MenuItem value={0} disabled>
                        Seleccione una opcion
                      </MenuItem>
                      <MenuItem value={1}>Para Mi</MenuItem>
                      <MenuItem value={2}>Para Otra persona</MenuItem>
                    </Select>
                    {fieldErrors.riderOption && (
                      <FormHelperText>Seleccione una opcion</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {riderOption === 2 && (
                  <Grid item xs={12} sm={6}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={handleOpenOtherPersonModal}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleOpenOtherPersonModal();
                        }
                      }}
                      sx={{
                        border: "1px solid",
                        borderColor:
                          fieldErrors.otherPersonName ||
                          fieldErrors.otherPersonPhone
                            ? "error.main"
                            : "divider",
                        borderRadius: "4px",
                        minHeight: 56,
                        px: 1.5,
                        py: 0.75,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        overflow: "hidden",
                        cursor: "pointer",
                        transition: "border-color 0.2s ease",
                        "&:hover": {
                          borderColor: "primary.main",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold" }}
                        noWrap
                      >
                        {otherPersonPreview.name || "NOMBRE"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {otherPersonPreview.phone || "TELEFONO"}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <OriginAutocompleteField
                    value={addressOrigin}
                    onChange={(value) => {
                      clearFieldError("origin");
                      setAddressOrigin(value);
                    }}
                    onSelect={(value) => {
                      clearFieldError("origin");
                      handSelect(value);
                    }}
                    onInputChange={handleChange("weight")}
                    onMapClick={handleOpenOriginMapModal}
                    placeholder={t("origin")}
                    error={!!fieldErrors.origin}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DestinationAutocompleteField
                    value={destinationInputValue}
                    onChange={(value) => {
                      clearFieldError("destination");
                      setAddressDestination(value);
                    }}
                    onSelect={(value) => {
                      clearFieldError("destination");
                      handleSelectDestination(value);
                    }}
                    onInputChange={handleChange("weight")}
                    onInputClick={handleOpenDestinationRoutes}
                    onMapClick={handleOpenDestinationMapModal}
                    placeholder={t("destination")}
                    readOnly={subRutes.length > 0}
                    hideRoutesIcon={subRutes.length >= 2}
                    error={!!fieldErrors.destination}
                  />
                </Grid>

                <SubRoutesModal
                  open={openSubRutesModal}
                  onClose={handleCloseSubRutesModal}
                  addressOrigin={addressOrigin}
                  subRutes={subRutes}
                  subRouteSearch={subRouteSearch}
                  subRouteError={subRouteError}
                  onSubRouteSearchChange={(value) => {
                    setSubRouteSearch(value);
                    setSubRouteError("");
                    setSubRouteSelected(null);
                  }}
                  onSelectSubRoute={handleSelectSubRoute}
                  onAcceptSubRoute={handleAcceptSubRoute}
                  onRemoveSubRoute={handleRemoveSubRoute}
                  onDragOver={handleSubRutesDragOver}
                  onDragEnd={handleSubRutesDragEnd}
                  destinationPlaceholder={t("destination")}
                  onMapClick={handleOpenSubRouteMapModal}
                />

                <BookingDetailsFields
                  t={t}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  model={model}
                  date={date}
                  onDateChange={(newValue) => {
                    clearFieldError("date");
                    setDate(newValue);
                    setDateEnd(newValue);
                  }}
                  time={time}
                  onTimeChange={(newValue) => {
                    clearFieldError("time");
                    setTime(newValue);
                  }}
                  dateEnd={dateEnd}
                  onDateEndChange={(newValue) => {
                    clearFieldError("dateEnd");
                    setDateEnd(newValue);
                  }}
                  timeEnd={timeEnd}
                  onTimeEndChange={(newValue) => {
                    clearFieldError("timeEnd");
                    setTimeEnd(newValue);
                  }}
                  countries={countries}
                  country={country}
                  onCountryChange={(newValue) => {
                    clearFieldError("country");
                    setCountry(newValue);
                  }}
                  tours={tours}
                  tour={tour}
                  onTourChange={(newValue) => {
                    clearFieldError("tour");
                    setTour(newValue);
                  }}
                  fieldErrors={fieldErrors}
                  clearFieldError={clearFieldError}
                />

                <Grid
                  item
                  xs={isBookingActive ? 10 : 9}
                  sm={isBookingActive ? 10 : 11}
                >
                  <Button
                    fullWidth
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ height: 45 }}
                    disabled={isLoading}
                  >
                    <Typography variant="string">
                      {values.booking === 1 ? t("book_now") : t("book_later")}
                    </Typography>
                  </Button>
                </Grid>
                <Grid item xs={1}>
                  <Button
                    fullWidth
                    color="success"
                    variant="contained"
                    sx={{ height: 45 }}
                  >
                    {values.total ?? 0}
                  </Button>
                </Grid>

                {isBookingActive && (
                  <Grid item xs={1}>
                    <Button
                      fullWidth
                      type="submit"
                      color="primary"
                      variant="contained"
                      sx={{
                        height: 45,
                        display: "flex",
                        justifyContent: "space-between",
                        padding: 0,
                      }}
                      disabled={isLoading}
                      onClick={(e) => {
                        e.preventDefault();
                        handleOpenOffer();
                      }}
                    >
                      <p
                        className={loadingStyle.loader}
                        style={{ height: 30, width: 30, margin: "auto" }}
                      />

                      <p style={{ fontSize: "18px", fontWeight: "bold" }}>
                        {offersList.length}
                      </p>
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
            <SnackBar
              stateSnk={stateSnk}
              vertical={vertical}
              horizontal={horizontal}
              duration={4000}
              handleClose={handleSnkClose}
              severity={severity}
              messageSnk={messageSnk}
            />
          </form>
        )}
      </Formik>

      <Modal open={openRadarAnimation} onClose={handleCloseRadarAnimation}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
          }}
        >
          <RadarAnimation />
        </Box>
      </Modal>

      <OriginMapModal
        open={openOriginMapModal}
        onClose={handleCloseOriginMapModal}
        addressOrigin={addressOrigin}
        coordOrigin={coordOrigin}
        onAddressChange={setAddressOrigin}
        onCoordinateChange={setCoordOrigin}
      />

      <OriginMapModal
        open={openDestinationMapModal}
        onClose={handleCloseDestinationMapModal}
        address={addressDestination}
        coordinate={coordDestination}
        onLocationAccept={({ address, point }) => {
          handleSelectDestinationPoint(address, point);
        }}
      />

      <OriginMapModal
        open={openSubRouteMapModal}
        onClose={handleCloseSubRouteMapModal}
        address={subRouteSearch}
        coordinate={subRouteMapCoordinate}
        onLocationAccept={({ address, point }) => {
          handleSelectSubRoutePoint(address, point);
          handleCloseSubRouteMapModal();
        }}
      />

      <OtherPersonModal
        open={openOtherPersonModal}
        onClose={handleCloseOtherPersonModal}
        otherPersonName={otherPersonName}
        otherPersonPhone={otherPersonPhone}
        onNameChange={(event) => {
          clearFieldError("otherPersonName");
          handleOtherPersonNameChange(event);
        }}
        onPhoneChange={(event) => {
          clearFieldError("otherPersonPhone");
          handleOtherPersonPhoneChange(event);
        }}
        onAccept={handleAcceptOtherPerson}
        fieldErrors={fieldErrors}
      />

      <OffersModal
        open={openOffers}
        onClose={handleCloseOffer}
        bookingOfferDetail={bookingOfferDetail}
        acceptedDriver={driverSelect}
        offersList={offersList}
        cancellationReasons={cancellationReasons}
        offersListMaxHeight={{ xs: "35vh", sm: "320px" }}
        onAcceptCounterOffer={acceptCounterOffer}
        onCancelBooking={cancelBooking}
      />

      <BookingSuccessModal
        open={bookingSuccess.open}
        message={bookingSuccess.message}
        orderCounter={bookingSuccess.orderCounter}
        onClose={handleCloseBookingSuccess}
        onViewHistory={handleViewBookingHistory}
      />
    </>
  );
};

export default BookingNow;
